import type { EndpointBuilder } from '@reduxjs/toolkit/query'

import { rootApi } from 'root-api-graphql'

import {
  MEETINGS_TAG_TYPES,
  buildMeetingListTag,
  buildMeetingTag,
} from './constants'
import { normalizeMeetingsGraphqlInputVariables } from './normalize_meetings_graphql_input_variables'
import type {
  MeetingsGraphQLResult,
  GraphQLMeetingResult,
} from './types'

/** Variables for cmd-api `listMeetings` (`Meetings_ListMeetingsInput`, camelCase filter). */
export type SupergraphListMeetingsInput = any
/**
 * Variables for the search-then-filter `meetingsPageList` (`MeetingsSearch_MeetingsInput`).
 * App builders may set `search_keyword` as a **sibling** of `filter`; it is merged into
 * `filter.search_keyword` just before the request (see {@link normalizeMeetingsGraphqlInputVariables}).
 */
export type SupergraphMeetingsPageListInput = any
export type SupergraphMeetingsConnection = MeetingsGraphQLResult
export type SupergraphMeeting = GraphQLMeetingResult
export type SupergraphGetMeetingArg = { id: string; organizationUuid?: string }
export type SupergraphGetMeetingByEitherIdArg = { meetingId: string }
export type SupergraphGetScheduledEventArg = { id: string }
export type SupergraphFilterCountPath = 'listMeetings' | 'meetingsPageList'
export type SupergraphFilterCountRequest = {
  path: SupergraphFilterCountPath
  inputs: SupergraphListMeetingsInput[] | SupergraphMeetingsPageListInput[]
}

/** Right-rail / refresh single meeting via cmd `listMeetings` + `filter.uuids`. */
export const buildMeetingByIdSupergraphInput = (id: string) => ({
  filter: { uuids: [id] },
  pagination: { first: 1, after: null },
})

/** Recap / foreign-event lookup via cmd `listMeetings` + `filter.externalUuids`. */
export const buildMeetingByEitherIdSupergraphInput = (meetingId: string) => ({
  filter: {
    externalUuids: [meetingId],
    uuids: [meetingId],
  },
  pagination: { first: 1, after: null },
})

/** From `SUPERGRAPH_BASE_URL` at webpack build (`define.mjs`); else `/api/supergraph/graphql`. */
export const getSupergraphBaseUrl = () => {
  const value = typeof window !== 'undefined'
    && (window as any).SUPERGRAPH_BASE_URL

  return (value && String(value)) || '/api/supergraph/graphql'
}

const PARTICIPANT_ROLE_TO_ATTENDEE_KIND: Record<string, string> = {
  HOST: 'HOST',
  COHOST: 'COHOST',
  ATTENDEE: 'INVITEE',
}

/** cmd `CMDMeeting.participants` → the `attendees` shape `mapNodeToMeeting` expects. */
const mapParticipantsToAttendees = (participants: any): any[] => {
  if (!Array.isArray(participants)) return []
  return participants.map((participant: any) => ({
    kind: PARTICIPANT_ROLE_TO_ATTENDEE_KIND[participant?.role] ?? 'INVITEE',
    name: participant?.name,
    email: participant?.email,
    uuid: participant?.identifier,
    cancelled: participant?.status === 'DECLINED',
    noShow: Boolean(participant?.noShow),
    rescheduled: Boolean(participant?.rescheduled),
  }))
}

/**
 * `listMeetings` / `meetingsPageList` return the flat `Meetings_CMDMeeting` node
 * (`title`, `startTime`, `participants`, `eventType`, `recapStatus`). Normalize it to the
 * snake-cased / `attendees` / `meeting_intelligence` shape `mapNodeToMeeting` consumes. Already-rich
 * (`Meetings_Meeting`) nodes pass through unchanged via the `??` fallbacks.
 */
const normalizeSupergraphMeetingNode = (node: any): any => {
  if (node == null || typeof node !== 'object') return node

  const attendees = Array.isArray(node.attendees) && node.attendees.length > 0
    ? node.attendees
    : mapParticipantsToAttendees(node.participants)

  return {
    ...node,
    name: node.name ?? node.title,
    start_time: node.start_time ?? node.startTime,
    end_time: node.end_time ?? node.endTime,
    owner_user_uuid: node.owner_user_uuid ?? node.ownerUuid,
    location_kind: node.location_kind ?? node.locationKind,
    event_type: node.event_type ?? node.eventType,
    meeting_intelligence: node.meeting_intelligence ?? node.recapStatus,
    invitees_count: node.invitees_count ?? node.inviteesCount,
    invitees_limit: node.invitees_limit ?? node.inviteesLimit,
    attendees,
  }
}

const withRandomKey = (node: any) => ({
  ...normalizeSupergraphMeetingNode(node),
  key:
    typeof crypto !== 'undefined' && (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : node?.uuid,
})

/**
 * `Meetings_ListMeetingsConnection` exposes edges on `meetings` (not `edges`). Map it to the
 * `{ meetings: { edges, pageInfo } }` shape the list orchestration consumes; tolerate a rich
 * connection that already uses `edges`.
 */
export const transformMeetingsSupergraphConnectionResponse = (response: any) => {
  const conn = response?.listMeetings
    ?? response?.meetingsPageList
    ?? response?.meetings
    ?? {}
  let rawEdges: any[] = []
  if (Array.isArray(conn.meetings)) {
    rawEdges = conn.meetings
  } else if (Array.isArray(conn.edges)) {
    rawEdges = conn.edges
  }
  const edges = rawEdges
    .filter((edge: any) => edge?.node != null)
    .map((edge: any) => ({
      ...edge,
      node: withRandomKey(edge.node),
    }))

  return {
    meetings: {
      ...conn,
      edges,
    },
  }
}

export const transformGetMeetingSupergraphResponse = (response: any) => {
  const node = response?.listMeetings?.meetings?.[0]?.node
    ?? response?.meetingsPageList?.meetings?.[0]?.node
    ?? response?.listMeetings?.edges?.[0]?.node
    ?? response?.meetings?.edges?.[0]?.node
    ?? null

  return node ? normalizeSupergraphMeetingNode(node) : null
}

/** Deep camelCase keys so scheduled-event JSON matches root-api REST transforms. */
const snakeToCamelKey = (key: string) => key.replace(/_([a-z0-9])/gi, (_, char: string) => char.toUpperCase())

const camelCaseKeysDeep = (value: unknown): Record<string, unknown> | unknown[] | unknown => {
  if (Array.isArray(value)) {
    return value.map(camelCaseKeysDeep)
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
        snakeToCamelKey(key),
        camelCaseKeysDeep(nested),
      ]),
    )
  }
  return value
}

/** `getScheduledEvent.event` is monolith scheduled-events JSON (same as REST GET). */
export const transformGetScheduledEventSupergraphResponse = (
  response: any,
): Record<string, unknown> | null => {
  const event = response?.getScheduledEvent?.event
  if (event == null || typeof event !== 'object') return null
  return camelCaseKeysDeep(event) as Record<string, unknown>
}

/** Count connection results by counting returned meeting nodes (no `total` on connection). */
const countConnectionMeetingNodes = (connection: any): number => {
  const meetings = connection?.meetings
  if (!Array.isArray(meetings)) return 0
  return meetings.filter((edge: any) => edge?.node?.uuid).length
}

/** cmd `Meetings_CMDMeeting` node selection (shared by `listMeetings` and `meetingsPageList`). */
const MEETING_NODE_FIELDS = `
  uuid
  kind
  ownerUuid
  title
  startTime
  endTime
  location
  locationKind
  cancelled
  externalSystemId
  inviteesCount
  inviteesLimit

  eventType {
    uuid
    name
    color
    duration
    kind
  }

  participants {
    email
    identifier
    name
    noShow
    rescheduled
    role
    status
  }

  recapStatus {
    uuid
    status
    error
  }
`

const CONNECTION_FIELDS = `
  meetings {
    cursor
    node {
      ${MEETING_NODE_FIELDS}
    }
  }
  pageInfo {
    startCursor
    endCursor
    hasNextPage
    hasPreviousPage
  }
`

// istanbul ignore next
const ListMeetingsDocument = `
  query ListMeetings($input: Meetings_ListMeetingsInput!) {
    listMeetings(input: $input) {
      ${CONNECTION_FIELDS}
    }
  }
`

// istanbul ignore next
const MeetingsPageListDocument = `
  query MeetingsPageList($input: MeetingsSearch_MeetingsInput!) {
    meetingsPageList(input: $input) {
      ${CONNECTION_FIELDS}
    }
  }
`

const MeetingsPageListCountDocument = `
  query MeetingsPageListCount($input: MeetingsSearch_MeetingsInput!) {
    meetingsPageList(input: $input) {
      meetings {
        node {
          uuid
        }
      }
    }
  }
`

const ListMeetingsCountDocument = `
  query ListMeetingsCount($input: Meetings_ListMeetingsInput!) {
    listMeetings(input: $input) {
      meetings {
        node {
          uuid
        }
      }
    }
  }
`

const GetScheduledEventDocument = `
  query GetScheduledEvent($id: ID!) {
    getScheduledEvent(id: $id) {
      event
    }
  }
`

export const meetingsSupergraphApi = rootApi
  .enhanceEndpoints({ addTagTypes: MEETINGS_TAG_TYPES })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, any, any>) => ({
      // Default meetings page / scope / non-recap filters → cmd-api `listMeetings`.
      listMeetingsSupergraph: build.query<
        SupergraphMeetingsConnection,
        SupergraphListMeetingsInput
      >({
        query: (input: SupergraphListMeetingsInput) => ({
          document: ListMeetingsDocument,
          variables: { input: normalizeMeetingsGraphqlInputVariables(input) },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformMeetingsSupergraphConnectionResponse,
        providesTags: (result) => {
          const tags = [buildMeetingListTag()]
          const edges = result?.meetings?.edges ?? []
          edges.forEach((edge: any) => {
            if (edge?.node?.uuid) {
              tags.push(buildMeetingTag(edge.node.uuid))
            }
          })
          return tags
        },
      }),

      // Keyword search / recap (meeting-intelligence) filter → search-then-filter `meetingsPageList`.
      meetingsPageListSupergraph: build.query<
        SupergraphMeetingsConnection,
        SupergraphMeetingsPageListInput
      >({
        query: (input: SupergraphMeetingsPageListInput) => ({
          document: MeetingsPageListDocument,
          variables: { input: normalizeMeetingsGraphqlInputVariables(input) },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformMeetingsSupergraphConnectionResponse,
        providesTags: (result) => {
          const tags = [buildMeetingListTag()]
          const edges = result?.meetings?.edges ?? []
          edges.forEach((edge: any) => {
            if (edge?.node?.uuid) {
              tags.push(buildMeetingTag(edge.node.uuid))
            }
          })
          return tags
        },
      }),

      // Filter-modal / action-bar count → listMeetings (default) or meetingsPageList (search/recap).
      searchMeetingsSupergraphFilterCount: build.query<
        number,
        SupergraphFilterCountRequest
      >({
        async queryFn({ path, inputs }, _api, _extraOptions, baseQuery) {
          const document = path === 'meetingsPageList'
            ? MeetingsPageListCountDocument
            : ListMeetingsCountDocument
          const rootField = path === 'meetingsPageList' ? 'meetingsPageList' : 'listMeetings'
          const countMeta = {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          }

          const results = await Promise.all((inputs ?? []).map((rawInput) => baseQuery({
            document,
            variables: {
              input: normalizeMeetingsGraphqlInputVariables(rawInput),
            },
            meta: countMeta,
          })))

          const failed = results.find((result) => result.error)
          if (failed?.error) {
            return { error: failed.error }
          }

          const total = results.reduce(
            (sum, result) => sum + countConnectionMeetingNodes((result.data as any)?.[rootField]),
            0,
          )

          return { data: total }
        },
        providesTags: [buildMeetingListTag()],
      }),

      // Right-rail single meeting → cmd-api `listMeetings` filtered by `uuids`.
      getMeetingSupergraph: build.query<
        SupergraphMeeting | null,
        SupergraphGetMeetingArg
      >({
        query: ({ id }) => ({
          document: ListMeetingsDocument,
          variables: { input: buildMeetingByIdSupergraphInput(id) },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformGetMeetingSupergraphResponse,
        providesTags: (result, _error, args) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.id) {
            tags.push(buildMeetingTag(args.id))
          }
          return tags
        },
        extraOptions: { handleErrors: false },
      }),

      // Right-rail publisher enrichment → monolith scheduled event via supergraph `getScheduledEvent`.
      getScheduledEventSupergraph: build.query<
        Record<string, unknown> | null,
        SupergraphGetScheduledEventArg
      >({
        query: ({ id }) => ({
          document: GetScheduledEventDocument,
          variables: { id },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformGetScheduledEventSupergraphResponse,
        providesTags: (_result, _error, args) => (
          args?.id ? [buildMeetingTag(args.id)] : [buildMeetingListTag()]
        ),
        extraOptions: { handleErrors: false },
      }),

      // Recap / foreign-event single meeting → cmd-api `listMeetings` filtered by `externalUuids`.
      getMeetingByEitherIdSupergraph: build.query<
        SupergraphMeeting | null,
        SupergraphGetMeetingByEitherIdArg
      >({
        query: ({ meetingId }) => ({
          document: ListMeetingsDocument,
          variables: { input: buildMeetingByEitherIdSupergraphInput(meetingId) },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformGetMeetingSupergraphResponse,
        providesTags: (result, _error, args) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.meetingId) {
            tags.push(buildMeetingTag(args.meetingId))
          }
          return tags
        },
        extraOptions: { handleErrors: false },
      }),
    }),
  })

export const {
  useListMeetingsSupergraphQuery,
  useLazyListMeetingsSupergraphQuery,
  useMeetingsPageListSupergraphQuery,
  useLazyMeetingsPageListSupergraphQuery,
  useSearchMeetingsSupergraphFilterCountQuery,
  useLazySearchMeetingsSupergraphFilterCountQuery,
  useGetMeetingSupergraphQuery,
  useLazyGetMeetingSupergraphQuery,
  useGetScheduledEventSupergraphQuery,
  useGetMeetingByEitherIdSupergraphQuery,
} = meetingsSupergraphApi
